/**
 * V525: 本地缓存策略 - 提升二次访问速度
 * V526: 分层缓存优化 - 基于数据时效性智能缓存
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  version: string;
  tier?: 'hot' | 'warm' | 'cold';
}

interface CacheConfig {
  ttl: number;
  version: string;
  storage: 'local' | 'session' | 'indexeddb';
  tier?: 'hot' | 'warm' | 'cold';
}

type DataTier = 'hot' | 'warm' | 'cold';

interface TierConfig {
  name: DataTier;
  ttl: number;
  storage: 'local' | 'session' | 'indexeddb';
  maxAge?: number;
}

export const TIER_CONFIGS: Record<DataTier, TierConfig> = {
  hot: {
    name: 'hot',
    ttl: 30 * 1000,
    storage: 'local',
    maxAge: 7 * 24 * 60 * 60 * 1000,
  },
  warm: {
    name: 'warm',
    ttl: 5 * 60 * 1000,
    storage: 'local',
    maxAge: 30 * 24 * 60 * 60 * 1000,
  },
  cold: {
    name: 'cold',
    ttl: 24 * 60 * 60 * 1000,
    storage: 'indexeddb',
    maxAge: Infinity,
  },
};

const DEFAULT_CONFIG: CacheConfig = {
  ttl: 5 * 60 * 1000,
  version: 'v1.0.0',
  storage: 'local',
};

class StatsCache {
  private readonly prefix = 'avalon_stats:';
  private readonly config: CacheConfig;
  private indexedDB: IDBDatabase | null = null;
  private dbReady: Promise<void> | null = null;

  constructor(config?: Partial<CacheConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  private async initIndexedDB(): Promise<void> {
    if (this.dbReady) return this.dbReady;

    this.dbReady = new Promise((resolve, reject) => {
      const request = indexedDB.open('AvalonStatsCache', 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.indexedDB = request.result;
        console.log('[V526-Cache] IndexedDB 初始化成功');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('cache')) {
          db.createObjectStore('cache', { keyPath: 'key' });
          console.log('[V526-Cache] 创建 IndexedDB cache store');
        }
      };
    });
  }

  private getKey(key: string): string {
    return `${this.prefix}${key}:${this.config.version}`;
  }

  private getStorage(): Storage {
    return this.config.storage === 'session' ? sessionStorage : localStorage;
  }

  private getDataTier(dateStr?: string): DataTier {
    if (!dateStr) return 'hot';

    const now = Date.now();
    const dataDate = new Date(dateStr).getTime();
    const age = now - dataDate;

    if (age <= TIER_CONFIGS.hot.maxAge!) return 'hot';
    if (age <= TIER_CONFIGS.warm.maxAge!) return 'warm';
    return 'cold';
  }

  private getConfigForTier(tier: DataTier): TierConfig {
    return TIER_CONFIGS[tier];
  }

  private isValid(entry: CacheEntry<any>): boolean {
    const now = Date.now();
    const ttl = entry.tier ? TIER_CONFIGS[entry.tier].ttl : this.config.ttl;
    return now - entry.timestamp < ttl;
  }

  async get<T>(key: string): Promise<T | null> {
    try {
      const storageKey = this.getKey(key);
      
      if (this.config.storage === 'indexeddb') {
        await this.initIndexedDB();
        return this.getFromIndexedDB(storageKey);
      }
      
      const cached = this.getStorage().getItem(storageKey);
      
      if (!cached) {
        console.log(`[V526-Cache] 未命中：${key}`);
        return null;
      }

      const entry: CacheEntry<T> = JSON.parse(cached);
      
      if (!this.isValid(entry)) {
        console.log(`[V526-Cache] 已过期：${key} (${entry.tier || 'default'}层)`);
        this.remove(key);
        return null;
      }

      console.log(`[V526-Cache] 命中：${key} (${entry.tier || 'default'}层，${Math.round((Date.now() - entry.timestamp) / 1000)}s 前)`);
      return entry.data;
    } catch (err) {
      console.error('[V526-Cache] 读取缓存失败:', err);
      return null;
    }
  }

  private async getFromIndexedDB<T>(key: string): Promise<T | null> {
    try {
      await this.initIndexedDB();
      if (!this.indexedDB) return null;

      return new Promise((resolve) => {
        const transaction = this.indexedDB!.transaction('cache', 'readonly');
        const store = transaction.objectStore('cache');
        const request = store.get(key);

        request.onsuccess = () => {
          const result = request.result;
          if (!result) {
            resolve(null);
            return;
          }

          const entry: CacheEntry<T> = result.value;
          if (!this.isValid(entry)) {
            this.removeFromIndexedDB(key);
            resolve(null);
            return;
          }

          resolve(entry.data);
        };

        request.onerror = () => {
          console.error('[V526-Cache] IndexedDB 读取失败:', request.error);
          resolve(null);
        };
      });
    } catch (err) {
      console.error('[V526-Cache] IndexedDB 读取异常:', err);
      return null;
    }
  }

  async set<T>(key: string, data: T, dateStr?: string): Promise<void> {
    try {
      const tier = this.getDataTier(dateStr);
      const tierConfig = this.getConfigForTier(tier);
      
      const storageKey = this.getKey(key);
      const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        version: this.config.version,
        tier,
      };

      if (tierConfig.storage === 'indexeddb') {
        await this.saveToIndexedDB(storageKey, entry);
      } else {
        const storage: Storage = tierConfig.storage === 'session' 
          ? sessionStorage 
          : localStorage;
        storage.setItem(storageKey, JSON.stringify(entry));
      }
      
      console.log(`[V526-Cache] 已缓存：${key} (${tier}层，${tierConfig.storage}存储，TTL=${tierConfig.ttl / 60000}分钟)`);
    } catch (err) {
      console.error('[V526-Cache] 写入缓存失败:', err);
      
      if (err instanceof DOMException && err.name === 'QuotaExceededError') {
        console.warn('[V526-Cache] 存储空间不足，清理旧缓存...');
        this.clearExpired();
        
        try {
          const storageKey = this.getKey(key);
          const entry: CacheEntry<T> = {
            data,
            timestamp: Date.now(),
            version: this.config.version,
          };
          this.getStorage().setItem(storageKey, JSON.stringify(entry));
        } catch (retryErr) {
          console.error('[V526-Cache] 重试写入失败:', retryErr);
        }
      }
    }
  }

  private async saveToIndexedDB<T>(key: string, entry: CacheEntry<T>): Promise<void> {
    await this.initIndexedDB();
    if (!this.indexedDB) return;

    return new Promise((resolve, reject) => {
      const transaction = this.indexedDB!.transaction('cache', 'readwrite');
      const store = transaction.objectStore('cache');
      const request = store.put({ key, value: entry });

      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.error('[V526-Cache] IndexedDB 写入失败:', request.error);
        reject(request.error);
      };
    });
  }

  private removeFromIndexedDB(key: string): void {
    if (!this.indexedDB) return;

    const transaction = this.indexedDB.transaction('cache', 'readwrite');
    const store = transaction.objectStore('cache');
    store.delete(key);
  }

  remove(key: string): void {
    try {
      const storageKey = this.getKey(key);
      
      if (this.config.storage === 'indexeddb') {
        this.removeFromIndexedDB(storageKey);
      } else {
        this.getStorage().removeItem(storageKey);
      }
      
      console.log(`[V526-Cache] 已移除：${key}`);
    } catch (err) {
      console.error('[V526-Cache] 移除缓存失败:', err);
    }
  }

  clearExpired(): void {
    try {
      const storage = this.getStorage();
      const keysToRemove: string[] = [];

      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (!key || !key.startsWith(this.prefix)) continue;

        try {
          const cached = storage.getItem(key);
          if (!cached) continue;

          const entry: CacheEntry<any> = JSON.parse(cached);
          if (!this.isValid(entry)) {
            keysToRemove.push(key);
          }
        } catch (err) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => storage.removeItem(key));
      console.log(`[V526-Cache] 清理了 ${keysToRemove.length} 条过期缓存`);
      
      this.clearExpiredFromIndexedDB();
    } catch (err) {
      console.error('[V526-Cache] 清理过期缓存失败:', err);
    }
  }

  private async clearExpiredFromIndexedDB(): Promise<void> {
    await this.initIndexedDB();
    if (!this.indexedDB) return;

    const transaction = this.indexedDB.transaction('cache', 'readwrite');
    const store = transaction.objectStore('cache');
    const request = store.openCursor();

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        const entry: CacheEntry<any> = cursor.value.value;
        if (!this.isValid(entry)) {
          cursor.delete();
          console.log(`[V526-Cache] 清理 IndexedDB 过期缓存：${cursor.key}`);
        }
        cursor.continue();
      }
    };
  }

  clearAll(): void {
    try {
      const storage = this.getStorage();
      const keysToRemove: string[] = [];

      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.startsWith(this.prefix)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => storage.removeItem(key));
      console.log(`[V526-Cache] 清空了 ${keysToRemove.length} 条缓存`);
      
      this.clearAllFromIndexedDB();
    } catch (err) {
      console.error('[V526-Cache] 清空缓存失败:', err);
    }
  }

  private async clearAllFromIndexedDB(): Promise<void> {
    await this.initIndexedDB();
    if (!this.indexedDB) return;

    const transaction = this.indexedDB.transaction('cache', 'readwrite');
    const store = transaction.objectStore('cache');
    store.clear();
    console.log('[V526-Cache] 清空 IndexedDB 缓存');
  }

  async getStats(): Promise<{ 
    total: number; 
    size: number; 
    oldest: number | null;
    indexedDBCount?: number;
  }> {
    try {
      const storage = this.getStorage();
      let total = 0;
      let size = 0;
      let oldest: number | null = null;

      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (!key || !key.startsWith(this.prefix)) continue;

        total++;
        const value = storage.getItem(key) || '';
        size += value.length;

        try {
          const entry: CacheEntry<any> = JSON.parse(value);
          if (oldest === null || entry.timestamp < oldest) {
            oldest = entry.timestamp;
          }
        } catch (err) {
          // ignore
        }
      }

      const indexedDBCount = await this.countIndexedDBEntries();

      return {
        total,
        size,
        oldest,
        indexedDBCount,
      };
    } catch (err) {
      console.error('[V526-Cache] 获取统计信息失败:', err);
      return { total: 0, size: 0, oldest: null };
    }
  }

  private async countIndexedDBEntries(): Promise<number> {
    await this.initIndexedDB();
    if (!this.indexedDB) return 0;

    return new Promise((resolve) => {
      const transaction = this.indexedDB!.transaction('cache', 'readonly');
      const store = transaction.objectStore('cache');
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(0);
    });
  }
}

// 导出单例实例
export const statsCache = new StatsCache({
  ttl: 5 * 60 * 1000,
  version: 'v1.0.0',
  storage: 'local',
});

export function createCache(config?: Partial<CacheConfig>): StatsCache {
  return new StatsCache(config);
}

export function getTierForDate(dateStr: string): DataTier {
  const now = Date.now();
  const dataDate = new Date(dateStr).getTime();
  const age = now - dataDate;

  if (age <= TIER_CONFIGS.hot.maxAge!) return 'hot';
  if (age <= TIER_CONFIGS.warm.maxAge!) return 'warm';
  return 'cold';
}

export function getTTLForTier(tier: DataTier): number {
  return TIER_CONFIGS[tier].ttl;
}

/**
 * V527: 判断日期是否超过 30 天（用于选择数据源）
 * @param dateStr 日期字符串 (YYYY-MM-DD)
 * @returns true 表示超过 30 天，应使用物化视图；false 表示近 30 天内，应使用原始表
 */
export function isBeyond30Days(dateStr: string): boolean {
  const now = Date.now();
  const dataDate = new Date(dateStr).getTime();
  const daysDiff = Math.floor((now - dataDate) / (1000 * 60 * 60 * 24));
  return daysDiff > 30;
}

/**
 * V527: 计算两个日期之间的天数差
 */
export function getDaysDifference(dateStr1: string, dateStr2: string): number {
  const d1 = new Date(dateStr1).getTime();
  const d2 = new Date(dateStr2).getTime();
  return Math.floor(Math.abs(d1 - d2) / (1000 * 60 * 60 * 24));
}

/**
 * V656: 数据版本控制 - 获取当前数据版本号
 */
export function getDataVersion(): number {
  const version = localStorage.getItem('avalon_data_version');
  return version ? parseInt(version, 10) : 0;
}

/**
 * V656: 数据版本控制 - 更新数据版本号
 */
export function incrementDataVersion(): number {
  const current = getDataVersion();
  const newVersion = current + 1;
  localStorage.setItem('avalon_data_version', newVersion.toString());
  console.log(`[V656] 数据版本已更新：${current} -> ${newVersion}`);
  return newVersion;
}

/**
 * V656: 数据版本控制 - 检查本地缓存版本是否过期
 * @param cachedVersion 缓存中存储的版本号
 * @returns true 表示版本过期，需要刷新
 */
export function isCacheVersionOutdated(cachedVersion: number): boolean {
  const currentVersion = getDataVersion();
  return cachedVersion < currentVersion;
}

/**
 * V656: 数据版本控制 - 清除所有统计缓存并重置版本检查
 */
export function clearAllStatsCache(): void {
  statsCache.clearAll();
  console.log('[V656] 已清除所有统计缓存');
}
